<template>
  <div class="stock-discovery-panel">
    <div class="panel-header">
      <h3 class="panel-title">{{ title }}</h3>
      <div class="search-box">
        <i class="pi pi-search"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search stocks..."
          class="search-input"
        />
      </div>
    </div>

    <div class="carousel-wrapper">
      <button
        v-show="canScrollLeft"
        class="nav-btn nav-btn-left"
        @click="scrollLeft"
        aria-label="Scroll left"
      >
        <i class="pi pi-chevron-left"></i>
      </button>

      <div class="stocks-scroll-container" ref="scrollContainer">
        <div class="stocks-row">
          <div
            v-for="stock in filteredStocks"
            :key="stock.symbol"
            class="stock-card"
            :class="{ selected: selectedStock === stock.symbol }"
            @click="$emit('stockSelected', stock.symbol)"
          >
            <div class="stock-symbol">{{ stock.symbol }}</div>
            <div class="stock-name">{{ stock.name }}</div>
            <div class="stock-meta">
              <span class="stock-sector">{{ stock.sector }}</span>
              <span class="stock-cap">{{ stock.capCategory }}</span>
            </div>
          </div>
        </div>
      </div>

      <button
        v-show="canScrollRight"
        class="nav-btn nav-btn-right"
        @click="scrollRight"
        aria-label="Scroll right"
      >
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  stocks: { type: Array, required: true },
  selectedSector: { type: String, default: null },
  selectedStock: { type: String, default: '' }
});

defineEmits(['stockSelected']);

const searchQuery = ref('');
const scrollContainer = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const title = computed(() => {
  return props.selectedSector ? `${props.selectedSector} Stocks` : 'All Stocks';
});

const filteredStocks = computed(() => {
  let stocks = props.stocks;

  if (props.selectedSector) {
    stocks = stocks.filter(s => s.sector === props.selectedSector);
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    stocks = stocks.filter(
      s => s.symbol.toLowerCase().includes(query) ||
           s.name.toLowerCase().includes(query)
    );
  }

  return stocks;
});

const checkScroll = () => {
  if (!scrollContainer.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 1;
};

const scrollLeft = () => {
  if (scrollContainer.value) {
    const cardWidth = 200; // Approximate card width + gap
    scrollContainer.value.scrollBy({ left: -cardWidth * 2, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  }
};

const scrollRight = () => {
  if (scrollContainer.value) {
    const cardWidth = 200;
    scrollContainer.value.scrollBy({ left: cardWidth * 2, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  }
};

onMounted(() => {
  checkScroll();
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', checkScroll);
  }
});

onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', checkScroll);
  }
});
</script>

<style scoped>
.stock-discovery-panel {
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}

.panel-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 200px;
  max-width: 300px;
  flex: 1;
}

.search-box i {
  color: #9ca3af;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
  color: #1f2937;
  min-width: 0;
}

.search-input::placeholder {
  color: #9ca3af;
}

.carousel-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.nav-btn-left {
  left: -12px;
}

.nav-btn-right {
  right: -12px;
}

.nav-btn:hover {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transform: translateY(-50%) scale(1.05);
}

.nav-btn i {
  font-size: 0.875rem;
  transition: color 0.2s;
}

.stocks-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 8px 0;
}

.stocks-scroll-container::-webkit-scrollbar {
  display: none;
}

.stocks-row {
  display: flex;
  gap: 14px;
  padding: 0 4px;
  width: fit-content;
}

.stock-card {
  flex: 0 0 auto;
  width: 170px;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  background: white;
  box-sizing: border-box;
}

.stock-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.stock-card.selected {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.25);
  transform: translateY(-3px);
}

.stock-symbol {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
}

.stock-name {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.stock-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;
  gap: 4px;
}

.stock-sector {
  color: #6b7280;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-cap {
  color: #3b82f6;
  font-weight: 600;
  flex-shrink: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stock-discovery-panel {
    padding: 16px 12px;
  }

  .panel-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    max-width: none;
  }

  .stock-card {
    width: 150px;
  }

  .nav-btn {
    width: 32px;
    height: 32px;
  }

  .nav-btn-left {
    left: -10px;
  }

  .nav-btn-right {
    right: -10px;
  }
}

@media (max-width: 480px) {
  .stock-discovery-panel {
    padding: 12px 8px;
  }

  .stock-card {
    width: 130px;
    padding: 10px;
  }

  .stock-symbol {
    font-size: 0.85rem;
  }

  .stock-name {
    font-size: 0.7rem;
  }

  .nav-btn {
    width: 28px;
    height: 28px;
  }
}
</style>
